// SYSTEM_ROUTE: /tasks
import { useState } from 'react';
import type { EntityId, Task, TaskLane } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { ScreenHeader } from '../app/ScreenHeader.js';
import { useAsync } from '../lib/useAsync.js';

const LANES: { lane: TaskLane; label: string; dot: string }[] = [
  { lane: 'now', label: 'Now', dot: 'var(--lc-primary)' },
  { lane: 'next', label: 'Next', dot: '#7aa2f7' },
  { lane: 'waiting', label: 'Waiting', dot: 'var(--lc-warn)' },
  { lane: 'scheduled', label: 'Scheduled', dot: '#c08cf0' },
  { lane: 'done', label: 'Done', dot: 'var(--lc-on-surface-variant)' },
];

const ADVANCE: Partial<Record<TaskLane, { to: TaskLane; label: string }>> = {
  next: { to: 'now', label: 'Start' },
  waiting: { to: 'next', label: 'To Next' },
  now: { to: 'done', label: 'Done' },
};

function LaneTitle({ dot, label }: { dot: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: dot, boxShadow: `0 0 0 3px color-mix(in srgb, ${dot} 18%, transparent)` }} />
      {label}
    </span>
  );
}

export function TasksScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');

  const lanes = useAsync(async () => {
    const entries = await Promise.all(LANES.map((l) => services.tasks.listByLane(l.lane)));
    return Object.fromEntries(LANES.map((l, i) => [l.lane, entries[i]])) as Record<TaskLane, readonly Task[]>;
  }, [revision]);

  const add = async () => {
    if (title.trim().length === 0) return;
    await services.tasks.create({ title, lane: 'next' });
    setTitle('');
    refresh();
  };

  const advance = async (id: EntityId, lane: TaskLane) => {
    const step = ADVANCE[lane];
    if (!step) return;
    if (step.to === 'done') await services.tasks.complete(id);
    else await services.tasks.move(id, step.to);
    refresh();
  };

  const data = lanes.value;
  const total = data ? Object.values(data).reduce((n, l) => n + l.length, 0) : 0;

  return (
    <>
      <ScreenHeader
        title="Tasks"
        subtitle="Execute and close loops — one lane at a time."
        actions={<Chip tone="muted">{total} total</Chip>}
      />

      <Pane title="Add a task">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: 1 }}
            value={title}
            placeholder="What needs doing?"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            aria-label="New task"
          />
          <Button variant="primary" onClick={() => void add()}>
            Add to Next
          </Button>
        </div>
      </Pane>

      <div className="lane-grid">
        {LANES.map(({ lane, label, dot }) => {
          const items = data?.[lane] ?? [];
          const step = ADVANCE[lane];
          return (
            <Pane key={lane} title={<LaneTitle dot={dot} label={label} />} hint={`${items.length}`}>
              <Stack>
                {items.length === 0 ? (
                  <EmptyState title="Clear">{lane === 'now' ? 'Pull your next task in when you start.' : 'Nothing here.'}</EmptyState>
                ) : (
                  items.map((t) => (
                    <CompactRow
                      key={String(t.id)}
                      title={t.title}
                      subtitle={t.scheduledFor ? new Date(t.scheduledFor).toLocaleString() : undefined}
                      meta={
                        step ? (
                          <Button variant={step.to === 'done' ? 'primary' : 'ghost'} onClick={() => void advance(t.id, lane)}>
                            {step.label}
                          </Button>
                        ) : undefined
                      }
                    />
                  ))
                )}
              </Stack>
            </Pane>
          );
        })}
      </div>
    </>
  );
}
